import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Phone, Mail, MessageCircle, ClipboardList, Calendar, FileText, Clock } from "lucide-react";

const BASE = process.env.REACT_APP_API_URL || "";

async function fetchTasks() {
  const res = await fetch(`${BASE}/tasks/all`, {
    credentials: 'include', // Use httpOnly cookie
  });
  if (!res.ok) throw new Error("Failed to fetch tasks");
  const json = await res.json();
  return json.data || [];
}

// Task type configurations
const TASK_TYPES = {
  call: { icon: Phone, color: 'bg-green-100 text-green-600' },
  email: { icon: Mail, color: 'bg-purple-100 text-purple-600' },
  sms: { icon: MessageCircle, color: 'bg-blue-100 text-blue-600' },
  follow_up: { icon: ClipboardList, color: 'bg-orange-100 text-orange-600' },
  meeting: { icon: Calendar, color: 'bg-pink-100 text-pink-600' },
  document: { icon: FileText, color: 'bg-yellow-100 text-yellow-600' },
  general: { icon: ClipboardList, color: 'bg-slate-100 text-slate-600' }
};

const statusColors = {
  overdue: "bg-red-100 text-red-700",
  dueToday: "bg-yellow-100 text-yellow-700",
  upcoming: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
};

function getTaskStatus(task) {
  if (task.status === "done" || task.status === "completed") return "completed";
  if (!task.dueDate) return "upcoming";

  const due = new Date(task.dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (due < today) return "overdue";
  if (due.toDateString() === today.toDateString()) return "dueToday";
  return "upcoming";
}

function formatDueDate(task, t) {
  if (!task.dueDate) return t('noDueDate');
  const due = new Date(task.dueDate);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (due.toDateString() === today.toDateString()) return t('today');
  if (due.toDateString() === tomorrow.toDateString()) return t('tomorrow');
  if (due < today) return t('overdue');

  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays <= 7) return `${diffDays}d`;
  return due.toLocaleDateString();
}

const TaskList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchTasks();
        // Show only incomplete tasks, sorted by due date, limit to 5
        const incomplete = data
          .filter(t => t.status !== "done" && t.status !== "completed")
          .sort((a, b) => {
            // Sort: overdue first, then by due date
            if (!a.dueDate && !b.dueDate) return 0;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return new Date(a.dueDate) - new Date(b.dueDate);
          })
          .slice(0, 5);
        setTasks(incomplete);
      } catch (err) {
        console.error("Failed to load tasks:", err);
      } finally {
        setLoading(false);
      }
    }
    load();

    // Listen for task updates
    const onUpdate = () => load();
    window.addEventListener("tasks:update", onUpdate);
    return () => window.removeEventListener("tasks:update", onUpdate);
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: "#007cf0" }}>
        <div className="text-lg font-semibold text-[#172A3A] mb-4">{t('tasks')}</div>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: "#007cf0" }}>
      <div
        className="flex items-center justify-between mb-4 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={() => navigate('/tasks')}
      >
        <div className="text-lg font-semibold text-[#172A3A]">{t('tasks')}</div>
        <ChevronRight className="text-gray-400" size={20} />
      </div>
      {tasks.length === 0 ? (
        <div className="text-gray-500 text-sm py-4 text-center">{t('noPendingTasks')}</div>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => {
            const status = getTaskStatus(task);
            const statusLabel = status === "dueToday" ? t('due') : status === "overdue" ? t('overdue') : t('upcoming');
            const typeConfig = TASK_TYPES[task.taskType] || TASK_TYPES.general;
            const TypeIcon = typeConfig.icon;

            return (
              <li
                key={task.id}
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 rounded-xl transition-colors border border-slate-100"
                onClick={() => navigate(`/tasks?taskId=${task.id}`)}
              >
                {/* Task Type Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
                  <TypeIcon size={18} />
                </div>

                {/* Task Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{task.title}</div>
                  <div className="flex items-center gap-2 mt-1">
                    {task.clientName && (
                      <span className="text-xs text-gray-500 truncate max-w-[100px]">{task.clientName}</span>
                    )}
                    {task.dueDate && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock size={10} />
                        {formatDueDate(task, t)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status Badge */}
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${statusColors[status]}`}>
                  {statusLabel}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default TaskList;
